/**
 * Remote-control wire protocol (phone -> emulator host). A phone on the same
 * network opens the standalone controller page (remote.html), which captures
 * touch input, folds it into the same Switch HidNpadButton bitfield the local
 * gamepad poller produces (see input.ts), and streams it to the host tab over a
 * transport (WebRTC DataChannel; see transport.ts). This module is the pure,
 * dependency-free codec for that stream — encode on the phone, decode on the
 * host — plus the pairing-URL helpers. It is deliberately transport-agnostic
 * and headlessly testable (test/remote-protocol.test.mjs).
 *
 * §1.6: input plumbing only; no Nintendo content, keys, or crypto here.
 */

/** Every frame begins with this byte so a receiver can reject foreign traffic. */
export const REMOTE_MAGIC = 0x56; /* 'V' */
/** Bumped only on an incompatible wire change; the decoder rejects mismatches. */
export const REMOTE_VERSION = 1;

/** Frame kinds. INPUT is the per-tick controller state; HELLO/BYE bracket a
 *  session so the host can show connect/disconnect without transport hooks. */
export const REMOTE_MSG_INPUT = 1;
export const REMOTE_MSG_HELLO = 2;
export const REMOTE_MSG_BYE = 3;

/** Fixed on-wire size of an INPUT frame, in bytes (see the layout below). */
export const REMOTE_INPUT_BYTES = 20;

/**
 * A decoded controller snapshot: the Switch HidNpadButton bitfield (see the
 * NPAD_* constants in input.ts) plus the four analog axes already packed into
 * the hardware's signed 16-bit range, and a monotonically-increasing sequence
 * number the host uses to drop reordered/stale frames.
 */
export interface RemoteInputState {
  seq: number;
  buttons: number;
  lx: number;
  ly: number;
  rx: number;
  ry: number;
}

/** Clamp to signed 16-bit — sticks are int16 on the hardware, and a hostile or
 *  buggy sender must never push an out-of-range word into the shared buffer. */
function clampS16(v: number): number {
  v = Math.round(v);
  return v < -32768 ? -32768 : v > 32767 ? 32767 : v;
}

/**
 * Encode an INPUT frame into a fresh 20-byte buffer. Layout (little-endian):
 *   0  u8   magic (0x56)
 *   1  u8   version (1)
 *   2  u8   type (1 = INPUT)
 *   3  u8   flags (reserved, 0)
 *   4  u32  seq
 *   8  u32  buttons (low 16 bits used today; u32 leaves room to grow)
 *   12 i16  lx   14 i16 ly   16 i16 rx   18 i16 ry
 * Sticks are clamped; seq/buttons are coerced to unsigned. Returns the bytes.
 */
export function encodeInput(state: RemoteInputState): Uint8Array<ArrayBuffer> {
  const buf = new Uint8Array(new ArrayBuffer(REMOTE_INPUT_BYTES));
  const dv = new DataView(buf.buffer);
  dv.setUint8(0, REMOTE_MAGIC);
  dv.setUint8(1, REMOTE_VERSION);
  dv.setUint8(2, REMOTE_MSG_INPUT);
  dv.setUint8(3, 0);
  dv.setUint32(4, state.seq >>> 0, true);
  dv.setUint32(8, state.buttons >>> 0, true);
  dv.setInt16(12, clampS16(state.lx), true);
  dv.setInt16(14, clampS16(state.ly), true);
  dv.setInt16(16, clampS16(state.rx), true);
  dv.setInt16(18, clampS16(state.ry), true);
  return buf;
}

/**
 * Decode an INPUT frame. Returns the snapshot, or null if the bytes are the
 * wrong length, carry the wrong magic/version, or are not an INPUT frame — the
 * wire is untrusted, so a malformed frame is rejected, never thrown on. Accepts
 * any ArrayBufferView (a DataChannel delivers ArrayBuffer / Uint8Array).
 */
export function decodeInput(data: ArrayBuffer | ArrayBufferView): RemoteInputState | null {
  const bytes =
    data instanceof ArrayBuffer
      ? new Uint8Array(data)
      : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  if (bytes.byteLength !== REMOTE_INPUT_BYTES) return null;
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (dv.getUint8(0) !== REMOTE_MAGIC) return null;
  if (dv.getUint8(1) !== REMOTE_VERSION) return null;
  if (dv.getUint8(2) !== REMOTE_MSG_INPUT) return null;
  return {
    seq: dv.getUint32(4, true),
    buttons: dv.getUint32(8, true),
    lx: dv.getInt16(12, true),
    ly: dv.getInt16(14, true),
    rx: dv.getInt16(16, true),
    ry: dv.getInt16(18, true),
  };
}

/** True if `next` is a newer frame than `last` under u32 wraparound (the seq
 *  counter is 32-bit and wraps after ~49 days at 1 kHz). Half-range compare. */
export function isNewer(next: number, last: number): boolean {
  return ((next - last) >>> 0) < 0x80000000;
}

/**
 * Build the pairing URL a phone opens to become a controller: the app's origin
 * + remote.html + the session id in the hash (kept out of the query so it never
 * reaches a server log). An optional `relay` signaling URL is embedded too, so
 * the phone inherits the same relay from the QR without the user typing it. The
 * host renders this as a link and a QR code.
 */
export function pairingUrl(origin: string, sessionId: string, relay?: string): string {
  const base = origin.replace(/\/+$/, "");
  const params = new URLSearchParams();
  params.set("s", sessionId);
  if (relay) params.set("relay", relay);
  return `${base}/remote.html#${params.toString()}`;
}

/** Extract the relay URL embedded in a pairing URL/hash, or null if absent. */
export function parsePairingRelay(url: string): string | null {
  const hash = url.includes("#") ? url.slice(url.indexOf("#") + 1) : url;
  const relay = new URLSearchParams(hash.replace(/^#/, "")).get("relay");
  return relay && relay.length > 0 ? relay : null;
}

/** Extract the session id from a pairing URL/hash, or null if absent/foreign.
 *  Accepts a full URL or a bare `#s=...` / `s=...` hash fragment. */
export function parsePairingUrl(url: string): string | null {
  const hash = url.includes("#") ? url.slice(url.indexOf("#") + 1) : url;
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const s = params.get("s");
  return s && s.length > 0 ? s : null;
}
